import { doc, getDocFromServer } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { PostViewer } from "../components/PostViewer";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspaceConnections } from "../hooks/useWorkspaceConnections";
import { withRequestDeadline } from "../lib/requestDeadline";
import { Post } from "../models/Post";
import { db } from "../services/Firebase";
import { postConverter } from "../services/converters";

const unavailable =
  "This post is unavailable, or your signed-in account no longer has access. Try the account that owns the post or belongs to its workspace.";

function LinkedPostViewer({
  post,
  onClose,
}: {
  post: Post;
  onClose: () => void;
}) {
  const { connections } = useWorkspaceConnections();
  const connectionsById = useMemo(
    () =>
      Object.fromEntries(
        connections.map((connection) => [connection.id, connection]),
      ),
    [connections],
  );
  return (
    <PostViewer
      isOpen
      expandedPost={post}
      initialIndex={0}
      connectionsById={connectionsById}
      onClose={onClose}
    />
  );
}

/** Own the deep link outside the gallery and its workspace-keyed layout.
 * Keeping viewPost in the URL also keeps OnboardingGate's continuation exemption
 * intact, so opening a post cannot unmount itself by consuming the parameter.
 */
export default function PrivatePostPage({ postId }: { postId: string }) {
  const {
    currentUser,
    loading,
    activeWorkspaceId,
    workspaceSwitching,
    switchWorkspace,
    logout,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    key: string;
    post?: Post;
    error?: string;
  } | null>(null);
  const key = JSON.stringify([
    currentUser?.uid,
    postId,
    activeWorkspaceId,
    attempt,
  ]);

  useEffect(() => {
    if (loading || !currentUser || !activeWorkspaceId) return;
    let cancelled = false;
    const controller = new AbortController();
    const load = async () => {
      try {
        // A server read rechecks membership instead of trusting an offline copy.
        const snapshot = await withRequestDeadline(
          () =>
            getDocFromServer(
              doc(db, "posts", postId).withConverter(postConverter),
            ),
          20000,
          "Opening this post took too long. Please try again.",
          controller.signal,
        );
        if (cancelled) return;
        if (!snapshot.exists()) throw new Error(unavailable);
        const post = { ...snapshot.data(), id: snapshot.id };
        const workspaceId = post.workspaceId || post.userId;
        if (!workspaceId) throw new Error(unavailable);
        // The document read and activation endpoint both enforce access. The
        // destination comes from the authorized document, never a URL hint.
        if (workspaceId !== activeWorkspaceId) {
          await switchWorkspace(workspaceId);
          // The new workspace reruns this effect before mounting viewer actions.
          return;
        }
        if (!cancelled) setResult({ key, post });
      } catch (error) {
        if (cancelled) return;
        const code = (error as { code?: string }).code;
        setResult({
          key,
          error:
            code === "permission-denied" || code === "not-found"
              ? unavailable
              : error instanceof Error
                ? error.message
                : "Couldn’t open this post. Please try again.",
        });
      }
    };
    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    currentUser,
    loading,
    activeWorkspaceId,
    postId,
    switchWorkspace,
    attempt,
    key,
  ]);

  const current = result?.key === key ? result : null;
  const close = () => {
    const params = new URLSearchParams(location.search);
    params.delete("viewPost");
    navigate(
      { pathname: "/created", search: params.toString() ? `?${params}` : "" },
      { replace: true },
    );
  };
  const changeAccount = async () => {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    try {
      await logout();
      navigate(`/login?${new URLSearchParams({ returnTo })}`, {
        replace: true,
      });
    } catch {
      setResult({ key, error: "Couldn’t sign out. Please try again." });
    }
  };

  return (
    <DashboardLayout>
      {current?.error ? (
        <section role="alert" className="mx-auto max-w-lg p-8">
          <h1 className="text-xl font-semibold">Unable to open this post</h1>
          <p className="my-4 text-sm text-muted-foreground">{current.error}</p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setAttempt((value) => value + 1)}>
              Try again
            </Button>
            <Button variant="outline" onClick={changeAccount}>
              Use another account
            </Button>
            <Button variant="ghost" onClick={close}>
              Back to Drafts
            </Button>
          </div>
        </section>
      ) : current?.post && !loading && !workspaceSwitching ? (
        <LinkedPostViewer key={key} post={current.post} onClose={close} />
      ) : (
        <p role="status" className="p-8 text-sm text-muted-foreground">
          Opening your post…
        </p>
      )}
    </DashboardLayout>
  );
}
